/**
 * Staff permission keys, granted through roles. `admin.<group>.<action>`; roles can use `*` or group
 * wildcards such as `admin.invoices.*`. Client sub-account permissions are a separate, smaller set.
 */
import { hasPermission } from './permission-match.js';

export const SETTINGS_CATEGORIES = Object.freeze([
  'general',
  'localization',
  'mail',
  'security',
  'ordering',
  'invoices',
  'payments',
  'tax',
  'support',
  'domains',
  'affiliates',
  'automation',
  'other',
]);

export const ADMIN_PERMISSIONS = {
  clients: ['read', 'create', 'update', 'delete', 'merge', 'impersonate', 'notes', 'credit', 'export', '2fa.reset'],
  contacts: ['manage'],
  orders: ['read', 'create', 'accept', 'cancel', 'delete', 'fraud'],
  services: ['read', 'update', 'delete', 'module', 'cancel', 'transfer'],
  domains: ['read', 'update', 'delete', 'registrar'],
  invoices: ['read', 'create', 'update', 'delete', 'payment', 'refund', 'credit_note', 'write_off', 'disputes'],
  quotes: ['read', 'manage'],
  transactions: ['read', 'manage'],
  billable_items: ['manage'],
  payment_methods: ['read', 'charge', 'delete'],
  products: ['read', 'manage'],
  promotions: ['manage'],
  currencies: ['manage'],
  tax: ['manage'],
  servers: ['read', 'manage'], // provisioning servers and server groups (Octail panels, etc.)
  tickets: ['read', 'reply', 'assign', 'delete', 'notes', 'merge'],
  support: ['departments', 'canned', 'escalations', 'kb', 'announcements', 'downloads', 'network_status', 'pages'],
  tasks: ['read', 'manage'], // the staff to-do list
  calendar: ['read'], // renewals, due invoices, domain expiries and tasks by date (each kind also needs its read)
  affiliates: ['read', 'manage', 'payouts'],
  reports: ['read', 'export'],
  email: ['read', 'templates', 'send', 'mass'],
  staff: ['read', 'manage'],
  roles: ['manage'],
  activity: ['read', 'export'],
  audit: ['read'],
  jobs: ['read', 'manage'],
  api_keys: ['manage'],
  webhooks: ['manage'],
  notifications: ['manage'], // staff notification channels (Discord, Slack)
  addons: ['manage'],
  gateways: ['manage'],
  fraud: ['manage'],
  bans: ['manage'],
  brands: ['read', 'manage'], // storefront brands (hostnames, logo, colours, sender)
  import: ['run'],
  system: ['health', 'backups', 'maintenance'],
  settings: SETTINGS_CATEGORIES.flatMap((c) => [`${c}.read`, `${c}.update`]),
};

/** What a client's sub-account (contact with a login) can be allowed to do. */
export const CONTACT_PERMISSIONS = Object.freeze([
  'profile',
  'contacts',
  'services',
  'services.manage', // module buttons, SSO into Octail, change password
  'domains',
  'domains.manage',
  'invoices',
  'payments', // pay invoices, manage payment methods, add funds
  'quotes',
  'tickets',
  'orders',
  'affiliates',
  'emails',
  'api', // client API keys and client webhooks (shared/src/client-api.js)
]);

/** Routes for these always ask for the password again (sudo mode). */
export const SUDO_ADMIN_PERMISSIONS = Object.freeze([
  'admin.api_keys.manage',
  'admin.clients.delete',
  'admin.clients.export',
  'admin.clients.merge',
  'admin.gateways.manage',
  'admin.import.run',
  'admin.invoices.write_off',
  'admin.payment_methods.charge',
  'admin.services.transfer',
  'admin.roles.manage',
  'admin.staff.manage',
  'admin.system.backups',
]);

const flatten = (groups, prefix = '') =>
  Object.entries(groups).flatMap(([group, actions]) => actions.map((a) => `${prefix}${group}.${a}`));

export const ADMIN_PERMISSION_KEYS = Object.freeze(flatten(ADMIN_PERMISSIONS, 'admin.'));
const adminSet = new Set(ADMIN_PERMISSION_KEYS);
export const isAdminPermission = (key) => adminSet.has(key);

export { hasPermission };

/** The concrete keys from `universe` both sets allow (API key scopes ∩ the owner's permissions). */
export const intersectPermissions = (a, b, universe) =>
  universe.filter((k) => hasPermission(a, k) && hasPermission(b, k));
